'use client'

import { useEffect, useState } from 'react'
import { Calendar, FileText, Euro } from 'lucide-react'

/**
 * Personalplanung showcase — loops between:
 *   0 — Schichtplan weekly grid (rows light up across the week)
 *   1 — Monthly hours totals per employee
 *   2 — Lohnabrechnung PDF preview with brutto amount
 */
const EMPLOYEES = [
  { name: 'Ayşe',   color: '#176a47', rate: 14.50, hours: 32 },
  { name: 'Mehmet', color: '#0c3e2a', rate: 16.00, hours: 36 },
  { name: 'Sven',   color: '#c2410c', rate: 15.00, hours: 28 },
  { name: 'Maria',  color: '#1f7752', rate: 13.50, hours: 24 },
]

const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

export function AnimatedShiftsFlow() {
  const [step, setStep] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (paused) return
    const t = setTimeout(() => setStep((step + 1) % 3), 3600)
    return () => clearTimeout(t)
  }, [step, paused])

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative rounded-2xl border border-pub-rule bg-pub-surface overflow-hidden"
      style={{ boxShadow: '0 20px 60px -20px rgba(23,106,71,0.18)' }}>

      <div className="flex items-center justify-between border-b border-pub-rule px-5 py-3 bg-pub-bg">
        <div className="flex items-center gap-2">
          {[Calendar, Calendar, FileText][step] && (() => {
            const Ic = [Calendar, Calendar, FileText][step]
            return <Ic className="h-3.5 w-3.5 text-brand-700" />
          })()}
          <p className="text-[12px] font-semibold text-pub-ink">
            {['Schichtplan', 'Monatsübersicht', 'Lohnabrechnung'][step]}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {[0, 1, 2].map(i => (
            <span key={i} className={`h-1 w-6 rounded-full transition-colors ${step === i ? 'bg-brand-600' : 'bg-pub-rule'}`} />
          ))}
        </div>
      </div>

      <div className="relative" style={{ height: 380 }}>
        {/* STEP 0 — Schichtplan grid */}
        <Pane visible={step === 0}>
          <div className="p-4">
            <div className="rounded-lg border border-pub-rule overflow-hidden">
              <div className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] bg-pub-bg border-b border-pub-rule">
                <div className="px-2 py-1.5 text-[9px] font-semibold uppercase tracking-widest text-pub-muted">Team</div>
                {DAYS.map(d => <div key={d} className="px-1 py-1.5 text-center text-[10px] font-semibold text-pub-ink">{d}</div>)}
              </div>
              {EMPLOYEES.map((emp, ei) => (
                <div key={emp.name} className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] border-b border-pub-rule last:border-0 bg-white">
                  <div className="px-2 py-2 flex items-center gap-1.5">
                    <span className="h-5 w-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold" style={{ background: emp.color }}>
                      {emp.name.charAt(0)}
                    </span>
                    <span className="text-[11px] font-medium text-pub-ink">{emp.name}</span>
                  </div>
                  {DAYS.map((_, di) => {
                    // Pre-determined shift pattern that varies per employee
                    const has = (ei + di) % 3 !== 2
                    return (
                      <div key={di} className="px-1 py-1.5 flex items-center justify-center">
                        {has && (
                          <div className="w-full rounded text-center text-[8px] font-semibold py-0.5 px-0.5 leading-tight"
                            style={{
                              background: emp.color + '22',
                              color: emp.color,
                              animation: `rzr-shift-in 0.4s ease-out ${0.15 * (ei * 7 + di)}s both`,
                            }}>
                            {ei % 2 === 0 ? '08-16' : '17-23'}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
            <p className="mt-3 text-[10.5px] text-pub-muted text-center">
              Drag &amp; drop · sortierbar nach Schichtbeginn
            </p>
          </div>
        </Pane>

        {/* STEP 1 — Monthly totals */}
        <Pane visible={step === 1}>
          <div className="p-5 space-y-2">
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="rounded-lg bg-pub-bg border border-pub-rule p-3">
                <p className="text-[10px] uppercase tracking-widest text-pub-muted">Stunden</p>
                <p className="text-2xl font-bold text-pub-ink" style={{ fontFamily: 'var(--font-display)' }}>120</p>
              </div>
              <div className="rounded-lg bg-pub-bg border border-pub-rule p-3">
                <p className="text-[10px] uppercase tracking-widest text-pub-muted">Personalkosten</p>
                <p className="text-2xl font-bold text-brand-700" style={{ fontFamily: 'var(--font-display)' }}>1.760 €</p>
              </div>
            </div>
            {EMPLOYEES.map((e, i) => (
              <div key={e.name}
                className="flex items-center justify-between rounded-lg border border-pub-rule bg-white px-3 py-2"
                style={{ animation: `rzr-row-in 0.45s ease-out ${0.1 * i}s both` }}>
                <div className="flex items-center gap-2">
                  <span className="h-6 w-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold" style={{ background: e.color }}>
                    {e.name.charAt(0)}
                  </span>
                  <span className="text-[12px] font-medium text-pub-ink">{e.name}</span>
                </div>
                <div className="flex items-center gap-3 text-[11px]">
                  <span className="text-pub-muted">{e.hours}h</span>
                  <span className="text-pub-muted">{e.rate.toFixed(2).replace('.', ',')}€/h</span>
                  <span className="font-bold text-pub-ink">{(e.hours * e.rate).toFixed(0)}€</span>
                </div>
              </div>
            ))}
          </div>
        </Pane>

        {/* STEP 2 — Lohnabrechnung */}
        <Pane visible={step === 2}>
          <div className="p-5">
            <div className="rounded-lg border border-pub-rule bg-white p-5"
              style={{ animation: 'rzr-pop 0.4s ease-out both' }}>
              <div className="text-center mb-3">
                <p className="text-[10px] uppercase tracking-widest text-pub-muted">Lohnabrechnung · Mai 2026</p>
                <p className="text-base font-bold text-pub-ink mt-0.5">Ayşe Yılmaz</p>
                <p className="text-[10px] text-pub-muted">Mini-Job (520€)</p>
              </div>
              <div className="border-t border-b border-pub-rule py-3 my-3 space-y-1.5 text-[11.5px]">
                <Row label="Geleistete Arbeitsstunden" value="32 Std." />
                <Row label="Stundenlohn" value="14,50 €" />
                <Row label="Basis" value="464,00 €" />
                <Row label="Bonus / Trinkgeld" value="20,00 €" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-semibold text-pub-muted">Bruttoauszahlung</span>
                <span className="text-2xl font-bold text-brand-700" style={{ fontFamily: 'var(--font-display)' }}>484 €</span>
              </div>
              <div className="mt-4 flex items-center justify-center gap-1.5 rounded-md bg-brand-600 py-2 text-[11.5px] font-semibold text-white">
                <Euro className="h-3.5 w-3.5" /> PDF herunterladen
              </div>
            </div>
          </div>
        </Pane>
      </div>

      <style>{`
        @keyframes rzr-shift-in { from { opacity: 0; transform: translateY(2px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes rzr-row-in { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes rzr-pop { from { transform: scale(0.94); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-pub-muted">{label}</span>
      <span className="font-medium text-pub-ink">{value}</span>
    </div>
  )
}

function Pane({ visible, children }: { visible: boolean; children: React.ReactNode }) {
  return (
    <div
      aria-hidden={!visible}
      className="absolute inset-0 transition-all duration-500"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
        pointerEvents: visible ? 'auto' : 'none',
      }}>
      {children}
    </div>
  )
}
